import type { Sprite } from 'pixi.js';
import { CHARGING_CHICK, CHICK } from '../../sim/full/attacks/brood';
import type { EnemyState, PlayerState, PropState, SimEvent, SimState } from '../../sim/state';
import { createBurster } from '../class-bursts';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { lerp, smoothstep } from '../../shared/scalar';
import { type Shape, type Sheet, TAU, paintSheet, paintedLater } from '../paint';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, byId, hide, lookup, setTint } from '../util';
import { ViewPool } from '../views';
import { type ChickSprites, chickSprites, placeChick } from './poussin';
import { CHICK_PIECES, UNIT } from './poussin-paint';

type ChickShapes = Readonly<Record<keyof typeof CHICK_PIECES, Shape>>;

const SIZE = 1.5;
const HOP = { scale: 0.1, perBeat: 1.5 } as const;
const PATTER = { perBeat: 2, calm: 0.5 } as const;
const STANDING = 0.3;
const LINE_UP_TICKS = 5;
const DUST = { puffs: 3, gap: 4, alpha: 0.45, colour: 0xe6d2aa } as const;
const LUNGE = { ticks: 3, reach: 2.5, calm: 0.5 } as const;
const PECK = { ticks: 5, from: 2, to: 9, peak: 0.55 } as const;
const TRAMPLE = { ticks: 6, from: 0.6, to: 1.8, peak: 0.8 } as const;
const TRAMPLE_REACH = 48;
const STARS = { count: 3, orbit: 1.1, tilt: 0.35, above: 5, size: 0.3, turnsPerBeat: 0.7 };

interface ChickView {
  readonly chick: ChickSprites;
  readonly dust: readonly Sprite[];
}

interface Track {
  kind: string;
  hitsLeft: number;
  heading: number;
  x: number;
  y: number;
  shiftX: number;
  shiftY: number;
  since: number;
  peckedAt: number;
  seen: number;
}

export function createBand(ctx: RenderContext): Family {
  const { layers, textures } = ctx;
  const sheet = paintedLater((): Sheet<keyof typeof CHICK_PIECES> => paintSheet(CHICK_PIECES));
  const burster = createBurster(ctx, 48);
  const tracks = new Map<number, Track>();
  const knocked = new Set<number>();
  let views: ViewPool<ChickView> | undefined;
  let starViews: ViewPool<readonly Sprite[]> | undefined;
  let image = 0;

  function viewsOf(shapes: ChickShapes): ViewPool<ChickView> {
    views ??= new ViewPool<ChickView>(
      () => ({
        dust: Array.from({ length: DUST.puffs }, () => add(layers.weapons, shapes.dust)),
        chick: chickSprites(layers.weapons, shapes),
      }),
      (view) => {
        hide(view.chick.body, view.chick.left, view.chick.right, ...view.dust);
      },
    );
    return views;
  }

  function starsOf(shapes: ChickShapes): ViewPool<readonly Sprite[]> {
    starViews ??= new ViewPool<readonly Sprite[]>(
      () => Array.from({ length: STARS.count }, () => add(layers.fx, shapes.dust)),
      (sprites) => {
        hide(...sprites);
      },
    );
    return starViews;
  }

  function trackOf(prop: PropState, owner: PlayerState, x: number, y: number, frame: Frame): Track {
    let track = tracks.get(prop.id);
    if (track === undefined) {
      track = {
        kind: prop.kind,
        hitsLeft: prop.hitsLeft,
        heading: Math.atan2(owner.aim.y, owner.aim.x),
        x,
        y,
        shiftX: 0,
        shiftY: 0,
        since: -Infinity,
        peckedAt: -Infinity,
        seen: image,
      };
      tracks.set(prop.id, track);
    }
    if (track.kind !== prop.kind && prop.kind === CHARGING_CHICK) {
      track.shiftX = track.x - x;
      track.shiftY = track.y - y;
      track.since = frame.now;
    }
    if (prop.kind === CHICK && prop.phase !== 'held' && prop.hitsLeft < track.hitsLeft) {
      track.peckedAt = frame.now;
      const tint = frame.palette[lookup(ctx.classTokens, owner.classId, 'class')];
      flare(PECK, prop.x, prop.y, frame, tint);
    }
    track.kind = prop.kind;
    track.hitsLeft = prop.hitsLeft;
    track.seen = image;
    return track;
  }

  function flare(
    look: { ticks: number; from: number; to: number; peak: number },
    x: number,
    y: number,
    frame: Frame,
    tint: number,
  ): void {
    burster.spawn(frame, {
      shape: textures.ring,
      now: frame.now,
      duration: look.ticks,
      x,
      y,
      fromRadius: look.from,
      toRadius: look.to,
      tint,
      peak: look.peak,
    });
  }

  function nearCharge(state: SimState, x: number, y: number): boolean {
    return (state.props ?? []).some(
      (prop) =>
        prop.kind === CHARGING_CHICK &&
        (prop.x - x) * (prop.x - x) + (prop.y - y) * (prop.y - y) <= TRAMPLE_REACH * TRAMPLE_REACH,
    );
  }

  function drawChick(
    state: SimState,
    prop: PropState,
    owner: PlayerState,
    view: ChickView,
    alpha: number,
    frame: Frame,
  ): void {
    const { now, calm, tempo } = frame;
    const beats = now / tempo.ticksPerBeat;
    const charging = prop.kind === CHARGING_CHICK;
    // A charging chick runs straight on its line from the start: where it was drawn before, the
    // line-up below eases it in.
    const simX = charging ? prop.x - prop.vx * (1 - alpha) : lerp(prop.prevX, prop.x, alpha);
    const simY = charging ? prop.y - prop.vy * (1 - alpha) : lerp(prop.prevY, prop.y, alpha);
    const track = trackOf(prop, owner, simX, simY, frame);
    const lineUp = Math.max(0, 1 - (now - track.since) / LINE_UP_TICKS);
    const eased = smoothstep(lineUp);
    const x = simX + track.shiftX * eased;
    const y = simY + track.shiftY * eased;
    const dx = x - track.x;
    const dy = y - track.y;
    const moving = Math.hypot(prop.x - prop.prevX, prop.y - prop.prevY) > STANDING;
    const pecking =
      prop.phase === 'grounded' ? byId(state.enemies, prop.targetId ?? -1) : undefined;
    if (pecking !== undefined) {
      track.heading = Math.atan2(pecking.y - y, pecking.x - x);
    } else if (Math.hypot(dx, dy) > STANDING / 4) {
      track.heading = Math.atan2(dy, dx);
    }
    track.x = x;
    track.y = y;
    const size = (owner.radius / REFERENCE_RADIUS) * SIZE;
    const since = (now - track.peckedAt) / LUNGE.ticks;
    const lunge =
      since >= 0 && since < 1
        ? Math.sin(Math.PI * since) * LUNGE.reach * UNIT * size * (calm ? LUNGE.calm : 1)
        : 0;
    const hop =
      moving && !calm ? HOP.scale * Math.abs(Math.sin(Math.PI * HOP.perBeat * beats + prop.id)) : 0;
    const step = moving
      ? Math.sin(TAU * PATTER.perBeat * beats + prop.id) * (calm ? PATTER.calm : 1)
      : 0;
    placeChick(
      view.chick,
      x + Math.cos(track.heading) * lunge,
      y + Math.sin(track.heading) * lunge,
      track.heading,
      size * (1 + hop),
      step,
    );

    const dusty = charging && !calm;
    view.dust.forEach((puff, index) => {
      puff.visible = dusty;
      if (!dusty) {
        return;
      }
      const back = (index + 1) * DUST.gap * UNIT * size;
      const sway = Math.sin(now * 0.7 + index + prop.id) * UNIT * size;
      puff.position.set(
        x - Math.cos(track.heading) * back - Math.sin(track.heading) * sway,
        y - Math.sin(track.heading) * back + Math.cos(track.heading) * sway,
      );
      puff.rotation = 0;
      puff.scale.set(size * (1 - index * 0.2));
      puff.alpha = DUST.alpha * (1 - index / DUST.puffs);
      setTint(puff, DUST.colour);
    });
  }

  function drawStars(enemy: EnemyState, sprites: readonly Sprite[], alpha: number, frame: Frame) {
    const { now, calm, palette, tempo } = frame;
    const x = lerp(enemy.prevX, enemy.x, alpha);
    const y = lerp(enemy.prevY, enemy.y, alpha);
    const turn = calm ? 0 : (now / tempo.ticksPerBeat) * STARS.turnsPerBeat * TAU;
    const size = Math.max(2, enemy.radius * STARS.size) / (2.4 * UNIT);
    sprites.forEach((star, index) => {
      const angle = turn + (index * TAU) / STARS.count;
      star.visible = true;
      star.position.set(
        x + Math.cos(angle) * enemy.radius * STARS.orbit,
        y - enemy.radius - STARS.above + Math.sin(angle) * enemy.radius * STARS.tilt,
      );
      star.rotation = 0;
      star.scale.set(size);
      star.alpha = 1;
      setTint(star, palette.noyau);
    });
  }

  return {
    onEvent(event: SimEvent, state: SimState, frame: Frame): void {
      if (
        event.type !== 'enemyStatusStarted' ||
        (event.status !== 'frozen' && event.status !== 'slowed') ||
        !nearCharge(state, event.x, event.y)
      ) {
        return;
      }
      const enemy = byId(state.enemies, event.id);
      if (event.status === 'frozen') {
        knocked.add(event.id);
      }
      const radius = enemy?.radius ?? 12;
      burster.spawn(frame, {
        shape: textures.ring,
        now: frame.now,
        duration: TRAMPLE.ticks,
        x: event.x,
        y: event.y,
        fromRadius: radius * TRAMPLE.from,
        toRadius: radius * TRAMPLE.to,
        tint: frame.palette.noyau,
        peak: TRAMPLE.peak,
      });
    },
    reset(): void {
      tracks.clear();
      knocked.clear();
      starViews?.releaseAll();
      views?.releaseAll();
      burster.bursts.clear();
    },
    update(state: SimState, alpha: number, frame: Frame): void {
      const props = state.props;
      if (props === undefined && views === undefined) {
        return;
      }
      const { shapes } = sheet.get();
      const pool = viewsOf(shapes);
      image += 1;
      pool.begin();
      for (const prop of props ?? []) {
        const owner =
          prop.kind === CHICK || prop.kind === CHARGING_CHICK
            ? byId(state.players, prop.playerId)
            : undefined;
        if (owner !== undefined) {
          drawChick(state, prop, owner, pool.acquire(prop.id), alpha, frame);
        }
      }
      pool.end();
      for (const [id, track] of tracks) {
        if (track.seen !== image) {
          tracks.delete(id);
        }
      }
      const starPool = starsOf(shapes);
      starPool.begin();
      for (const id of knocked) {
        const enemy = byId(state.enemies, id);
        if (enemy === undefined || enemy.hp <= 0 || enemy.statuses?.frozen === undefined) {
          knocked.delete(id);
        } else {
          drawStars(enemy, starPool.acquire(id), alpha, frame);
        }
      }
      starPool.end();
      burster.bursts.update(state, alpha, frame);
    },
    destroy(): void {
      burster.bursts.clear();
      knocked.clear();
      sheet.painted?.source.destroy();
    },
  };
}
